import {
  Avatar,
  Box,
  Flex,
  Menu,
  Portal,
  Spinner,
  Text,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Icon } from 'src/components/atoms/Icon';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { MenuList } from 'src/components/molecules/MenuList';
import { useAppContext } from 'src/contexts/AppContext';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';
import { cleanCustomerName } from 'src/utils/cleanCustomerName';

const MENU_BUTTON_NAME = 'change-client';

const CustomerRow = ({
  label,
  isOpen,
  showTriangle,
}: {
  label: string | null;
  isOpen?: boolean;
  showTriangle?: boolean;
}) => (
  <Flex align="center" gap="3" px="3" py="3" w="full">
    <Avatar size="sm" name={label ?? undefined} />
    <Text
      fontFamily="marketingPage"
      fontSize={15}
      fontWeight="medium"
      color="sidebar.text"
      textTransform="capitalize"
      flex="1"
      minW={0}
      isTruncated>
      {label}
    </Text>
    {showTriangle && (
      <ChakraIcon
        as={isOpen ? ChevronDown : ChevronRight}
        boxSize="5"
        color="sidebar.textMuted"
      />
    )}
  </Flex>
);

/**
 * Customer switcher pinned at the bottom of the sidebar.
 */
export const SidebarCustomerSwitcher = () => {
  const { currentCustomer, setCurrentCustomer, customersList } =
    useAppContext();

  const fontErrorColor = useFontErrorColor();
  const label = cleanCustomerName(currentCustomer?.customerName ?? null);

  if (customersList?.isError) {
    return (
      <Flex alignItems="center" px="4" py="3" color={fontErrorColor}>
        <Icon iconName="warning" mr={2} />
        <Text fontSize="16px" fontWeight={700} lineHeight={1}>
          Error fetching customer view
        </Text>
      </Flex>
    );
  }

  if (customersList?.isLoading) {
    return (
      <Flex alignItems="center" px="4" py="3">
        <Spinner size="sm" mr={2} />
        <Text fontSize="16px" fontWeight={700} lineHeight={1}>
          Loading...
        </Text>
      </Flex>
    );
  }

  if (customersList?.customers?.length > 1) {
    return (
      <Box px="4">
        <Menu placement="top" matchWidth>
          {({ isOpen, onClose }) => (
            <>
              <MenuButton
                buttonName={MENU_BUTTON_NAME}
                w="full"
                textAlign="left"
                borderRadius="lg"
                _hover={{ bg: 'sidebar.itemHoverBg' }}>
                <CustomerRow label={label} isOpen={isOpen} showTriangle />
              </MenuButton>
              <Portal>
                <MenuList
                  bg="surface.panel"
                  w="full"
                  minW="unset"
                  maxW="unset"
                  zIndex="popover"
                  buttonName={MENU_BUTTON_NAME}>
                  {customersList?.customers?.map((customer) => {
                    const name = customer.customerName;
                    const optionLabel = cleanCustomerName(name);

                    if (!optionLabel) {
                      return null;
                    }

                    return (
                      <MenuOption
                        colorSelected
                        onClick={() => {
                          setCurrentCustomer(customer);
                          onClose();
                        }}
                        buttonName={MENU_BUTTON_NAME}
                        key={name}
                        value={name}
                        label={optionLabel}
                        isSelected={currentCustomer?.customerName === name}>
                        {optionLabel}
                      </MenuOption>
                    );
                  })}
                </MenuList>
              </Portal>
            </>
          )}
        </Menu>
      </Box>
    );
  }

  return (
    <Box px="4">
      <CustomerRow label={label} />
    </Box>
  );
};
